<script setup lang="ts">
//#region imports
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';
import { useApi } from '@/api';
import { useSessionAction } from '@/composables/useSessionAction';
import { usePopupManager } from '@/components/PopupManager.vue';
import { useEventBus } from '@/setup/eventBus';
import { useTypedStore } from '@/store';
import { useFilters } from '@/setup/filters';
import type { ConfirmParams } from '@/components/Popups/Confirm.vue';
import type { GetAdminsByIdResponse, GetTeamsByIdResponse } from '@/types/openapi-ts';
import type { CardItemType } from '@/components/CardComponents/CardItem.vue';
//#endregion

//#region types
type UserInfo = GetAdminsByIdResponse['payload'];
type TeamInfo = GetTeamsByIdResponse['payload'];
type ConfirmData = NonNullable<ConfirmParams['data']>;

type SensitiveAction =
  | 'handleUserPermission'
  | 'handleResetUserPassword'
  | 'handleResetUser2fa'
  | 'handleBlockUser'
  | 'handleUnblockUser';
//#endregion

//#region composables
const route = useRoute();
const api = useApi();
const eventBus = useEventBus();
const { t } = useI18n();
const { onSessionUnlock } = useSessionAction();
const router = useRouter();
const store = useTypedStore();
const popupManager = usePopupManager();
const filters = useFilters();
//#endregion

//#region state
const teamId = ref('');
const teamInfo = ref<Partial<TeamInfo>>({});
const userInfo = ref<UserInfo | null>(null);
const userStatus = ref('');

const isDataLoading = ref(false);
//#endregion

//#region computed
const timezones = computed(() => store.getters['misc/timezones']);

const seeStatsConf = computed(() => {
  let result: CardItemType = { name: t('user_info.see_stats_overall') };
  const from = userInfo.value?.entities_visibility_from;
  const to = userInfo.value?.entities_visibility_to;

  if (from && to) {
    result = {
      name: `${filters.formatDate4(from)} - ${filters.formatDate4(to)}`,
    };
  }

  if (!from && to) {
    result = {
      name: t('user_info.see_stats_to'),
      value: filters.formatDate4(to),
    };
  }

  if (from && !to) {
    result = {
      name: t('user_info.see_stats_from'),
      value: filters.formatDate4(from),
    };
  }

  return result;
});
const breadcrumbs = computed(() => [
  {
    title: teamId.value
      ? teamInfo.value.name
      : t('teams.teams_fields.default_team'),
    link: teamId.value
      ? `/teams/${teamId.value}/details/users`
      : '/teams/all/details/users',
  },
  {
    title: userInfo.value?.name ?? '',
  },
]);

const viewTimezone = computed(() => {
  const timezone = userInfo.value?.timezone;
  if (timezone && timezones.value) {
    return timezones.value.find(
      (item) => item.zone.includes(timezone),
    )?.name ?? 'Invalid time zone';
  }
  return 'Invalid time zone';
});

const userId = computed(() => {
  const id = route.params.id;
  return Array.isArray(id) ? (id[0] ?? '') : (id ?? '');
});

const isHide = computed(() => {
  if(userInfo.value) {
    return !userInfo.value.teams?.some((item) => item.type === 'owners')
      && Boolean(userInfo.value.teams_count && userInfo.value.teams_count > 0);
  }
  return true;
});
//#endregion

//#region methods
function getTeam(id: string) {
  api.getTeam(id).then((response) => {
    teamInfo.value = response.data.payload;
  });
}

function getAdmin() {
  if (!userId.value) {
    return;
  }

  api.getAdmin(userId.value).then((response) => {
    userInfo.value = response.data.payload;
    userStatus.value = response.data.payload.account_status;
  });
}

function changeStatus(val: string) {
  userStatus.value = val;
}

function initData() {
  isDataLoading.value = true;
  const queryTeamId = route.query.team_id;

  if (queryTeamId) {
    teamId.value = Array.isArray(queryTeamId) ? (queryTeamId[0] ?? '') : queryTeamId;
  } else {
    teamId.value = '';
  }

  if (teamId.value) {
    getTeam(teamId.value);
  }

  getAdmin();
  isDataLoading.value = false;
}
function checkSensitiveAction(action: SensitiveAction) {
  eventBus.$emit(action);
}

function openConfirm(data: ConfirmData, action: () => Promise<unknown>) {
  const confirmPopup = popupManager.confirm.open({
    data,
    listeners: {
      success: async () => {
        const context = await confirmPopup;

        try {
          await action();
          context.handleClose();
        } catch (error: unknown) {
          window.console.error(error);
        }
      },
    },
  });
}

function delFromSystemHandler() {
  openConfirm({
    title: t('team_popup.delete_title_system', { name: userInfo.value!.name }),
    message: t('team_popup.delete_msg_system', { name: userInfo.value!.name }),
    bodyIcon: 'trash-alt',
    bodyIconColor: 'var(--text-negative)',
    confirmIcon: 'trash-alt',
    confirmTitle: t('team_popup.delete_action_system'),
    confirmType: 'negative',
  }, delFromSystem);
}

async function delFromSystem() {
  await api.deleteAdmins(userId.value);
  await router.push(
    teamId.value
      ? `/teams/${teamId.value}/details/users`
      : '/teams/all/details/users',
  );
}

function permissionsHandler() {
  popupManager.userPermissions.open({
    data: {
      id: teamId.value || '',
      userInfo: userInfo.value!,
    },
  });
}

function resetUserPasswordHandler() {
  openConfirm({
    title: t('team_popup.reset_pass_title', { name: userInfo.value!.name }),
    message: t('team_popup.reset_pass_msg', { name: userInfo.value!.name }),
    bodyIcon: 'exclamation-triangle',
    bodyIconColor: 'var(--text-negative)',
    confirmIcon: 'check',
    confirmTitle: t('team_popup.reset_pass_action'),
    confirmType: 'negative',
  }, resetUserPassword);
}

async function resetUserPassword() {
  await api.postResetPassword(userId.value);
}

function blockHandler() {
  openConfirm({
    title: t('team_popup.block_title', { name: userInfo.value!.name }),
    message: t('team_popup.block_msg', { name: userInfo.value!.name }),
    bodyIcon: 'lock',
    bodyIconColor: 'var(--text-negative)',
    confirmIcon: 'lock',
    confirmTitle: t('team_popup.block_action'),
    confirmType: 'negative',
  }, blockUser);
}

async function blockUser() {
  await api.postBlock(userId.value);
  changeStatus('blocked');
}

function unblockHandler() {
  openConfirm({
    title: t('team_popup.unblock_title', { name: userInfo.value!.name }),
    message: t('team_popup.unblock_msg', { name: userInfo.value!.name }),
    bodyIcon: 'unlock',
    bodyIconColor: 'var(--text-positive)',
    confirmIcon: 'unlock',
    confirmTitle: t('team_popup.unblock_action'),
    confirmType: 'positive',
  }, unblockUser);
}

async function unblockUser() {
  await api.postUnblock(userId.value);
  changeStatus('active');
}

function reset2faHandler() {
  openConfirm({
    title: t('team_popup.reset_2fa_title', { name: userInfo.value?.name }),
    message: t('team_popup.reset_2fa_msg', { name: userInfo.value?.name }),
    bodyIcon: 'exclamation-triangle',
    bodyIconColor: 'var(--text-negative)',
    confirmIcon: 'check',
    confirmTitle: t('team_popup.reset_2fa_action'),
    confirmType: 'negative',
  }, reset2faUser);
}

async function reset2faUser() {
  await api.postResetTotp(userId.value);
}

function editUserHandler() {
  popupManager.editTeamsUser.open({
    data: {
      user: userInfo.value!,
    },
    listeners: {
      refresh: (user) => {
        userInfo.value = {
          ...userInfo.value,
          ...user,
        };
      },
    },
  });
}

function addUserHandler() {
  popupManager.attachUserToTeam.open({
    data: {
      context: {
        mode: 'select-team',
        userInfo: userInfo.value!,
      },
    },
    listeners: {
      refresh: () => getAdmin(),
    },
  });
}

function pinAffiliatesHandler(id: string) {
  popupManager.pinAffiliateUser.open({
    data: {
      teamId: id,
      userInfo: userInfo.value!,
    },
    listeners: {
      refresh: () => getAdmin(),
    },
  });
}

function delFromTeamHandler(id: string, teamName: string) {
  openConfirm({
    title: t('team_popup.delete_title_team', { name: userInfo.value?.name, teamName }),
    message: t('team_popup.delete_msg_team', { name: userInfo.value?.name, teamName }),
    bodyIcon: 'trash-alt',
    bodyIconColor: 'var(--text-negative)',
    confirmIcon: 'trash-alt',
    confirmTitle: t('team_popup.delete_action_team'),
    confirmType: 'negative',
  }, () => delFromTeam(id));
}

async function delFromTeam(id: string) {
  await api.postDetachFromTeam(userId.value, { team_id: id });
  getAdmin();
}


function unpinAffiliateHandler(id: string, name: string, team: string) {
  openConfirm({
    title: t('team_popup.unpin_title', { name: userInfo.value?.name, affiliateName: name }),
    message: t('team_popup.unpin_msg', { name: userInfo.value?.name, affiliateName: name }),
    bodyIcon: 'exclamation-triangle',
    bodyIconColor: 'var(--text-negative)',
    confirmIcon: 'unlink',
    confirmTitle: t('team_popup.unpin_action'),
    confirmType: 'negative',
  }, () => unpinAffiliate(id, team));
}

async function unpinAffiliate(id: string, team: string) {
  await api.postDetachAffiliate(userId.value, {
    affiliate_id: id,
    team_id: team,
  });
  getAdmin();
}
//#endregion

//#region watch
watch(
  [
    () => route.params.id,
    () => route.query.team_id,
  ],
  initData,
);
//#endregion

//#region hooks
onSessionUnlock.value = initData;

onMounted(() => {
  eventBus.$on('handleUserPermission', permissionsHandler);
  eventBus.$on('handleResetUserPassword', resetUserPasswordHandler);
  eventBus.$on('handleResetUser2fa', reset2faHandler);
  eventBus.$on('handleBlockUser', blockHandler);
  eventBus.$on('handleUnblockUser', unblockHandler);

  initData();
});

onBeforeUnmount(() => {
  // labelNameResizeObserver?.disconnect();
  eventBus.$off('handleUserPermission', permissionsHandler);
  eventBus.$off('handleResetUserPassword', resetUserPasswordHandler);
  eventBus.$off('handleResetUser2fa', reset2faHandler);
  eventBus.$off('handleBlockUser', blockHandler);
  eventBus.$off('handleUnblockUser', unblockHandler);
});
//#endregion
</script>

<template>
  <div
    v-loading="isDataLoading"
    class="user-info t:space-y-16"
  >
    <Breadcrumbs :items="breadcrumbs" />
    <UiToolbar>
      <template v-slot:title>
        <div class="t:flex t:items-center t:gap-8">
          {{ userInfo?.name }}
          <UiIcon
            :size="16"
            :name="userStatus === 'active' ? 'check' : 'ban'"
            :color="userStatus === 'active' ? 'var(--text-primary)' : 'var(--text-negative)'"
          />
        </div>
      </template>
    </UiToolbar>
    <div class="t:space-x-8">
      <UiButton
        v-if="isHide"
        icon="unlock"
        @click="checkSensitiveAction('handleUserPermission')"
      >
        {{ t(`team_users.list.actions.permissions`) }}
      </UiButton>
      <a
        :href="userInfo?.login_url"
        @click.stop.prevent
      >
        <UiButton icon="sign-in-alt">
          {{ t('affiliates.cardView.toolbar.btns.login') }}
        </UiButton>
      </a>
      <UiButton
        icon="key"
        type="negative"
        @click="checkSensitiveAction('handleResetUserPassword')"
      >
        {{ t(`team_users.list.actions.reset_password`) }}
      </UiButton>
      <UiButton
        icon="laptop-mobile"
        type="negative"
        @click="checkSensitiveAction('handleResetUser2fa')"
      >
        {{ t(`team_users.list.actions.reset_2fa`) }}
      </UiButton>

      <UiButton
        v-if="userStatus === 'active'"
        type="negative"
        icon="ban"
        @click="checkSensitiveAction('handleBlockUser')"
      >
        {{ t(`team_users.list.actions.block`) }}
      </UiButton>
      <UiButton
        v-else
        icon="check"
        @click="checkSensitiveAction('handleUnblockUser')"
      >
        {{ t(`team_users.list.actions.unblock`) }}
      </UiButton>
      <UiButton
        type="negative"
        icon="trash-alt"
        @click="delFromSystemHandler"
      >
        {{ t(`team_users.list.actions.delete_from_system`) }}
      </UiButton>
    </div>
    <UiSeparator />
    <div class="t:flex t:gap-32">
      <div class="user-info__block user-info__block_side t:space-y-24">
        <CardBlock
          :title="$t('user_info.personal_information')"
        >
          <template v-slot:action>
            <UiAction
              icon="pen"
              :action-name="t('user_info.edit')"
              color="var(--text-primary)"
              @click="editUserHandler"
            />
          </template>
          <template v-slot:cardItem>
            <CardItem
              no-value
              no-border
            >
              <template v-slot:title>
                <div class="t:space-y-8">
                  <UiTitle
                    :font-size="24"
                    class="t:max-w-full t:font-medium"
                  >
                    {{ userInfo?.name }}
                  </UiTitle>
                  <div
                    v-if="userInfo && (userInfo.last_seen_days_ago ?? 0) >= 14"
                    class="user-info__last-seen"
                    v-html="t('team_users.list.alert.last_seen', {
                      days: userInfo.last_seen_days_ago,
                    })"
                  />
                </div>
              </template>
            </CardItem>
            <CardItem
              no-value
              pre-icon="envelope"
            >
              <template v-slot:title>
                <div class="t:flex t:items-center t:gap-8">
                  <UiIcon
                    :size="14"
                    color="var(--text-primary)"
                    name="envelope"
                  />
                  <a
                    v-if="userInfo?.email"
                    :href="`mailto:${userInfo.email}`"
                    class="user-info__email t:truncate"
                    rel="noopener noreferrer"
                    target="_blank"
                  >{{ userInfo.email }}</a>
                </div>
              </template>
            </CardItem>
            <CardItem no-value>
              <template v-slot:title>
                <UiMessenger
                  v-if="userInfo?.messenger_id"
                  :messenger-type="userInfo.messenger!"
                  :messenger-address="userInfo.messenger_id!"
                  class="t:truncate"
                />
              </template>
            </CardItem>
            <CardItem
              no-value
              pre-icon="clock"
              :item="{name: viewTimezone}"
            />
          </template>
        </CardBlock>
        <CardBlock
          :title="$t('user_info.see_stats')"
        >
          <template v-slot:cardItem>
            <CardItem
              no-border
              :no-value="!seeStatsConf.value"
              :item="seeStatsConf"
            />
          </template>
        </CardBlock>
        <CardBlock
          :title="$t('user_info.login_reg')"
          :list="[
            {
              name: $t('user_info.last_login'),
              value: filters.formatDate3(userInfo?.last_active || ''),
            },
            {
              name: $t('user_info.reg_date'),
              value: filters.formatDate3(userInfo?.created_at || ''),
            }
          ]"
        />
        <CardBlock
          v-if="userInfo?.notes"
          :title="$t('user_info.note')"
        >
          <template v-slot:cardItem>
            <CardItem
              no-value
              no-border
              :item="{ name: userInfo.notes }"
            />
          </template>
        </CardBlock>
      </div>
      <div class="user-info__block t:space-y-16">
        <div class="t:flex t:items-center t:justify-between">
          <UiLabel
            :label="t(`user_info.teams`)"
            :font-size="22"
            bold
          />
          <UiButton
            icon="plus"
            @click="addUserHandler"
          >
            {{ t('user_info.add_team') }}
          </UiButton>
        </div>
        <div
          v-if="userInfo?.teams_count === 0"
          class="user-info__empty"
        >
          <UiIcon
            name="users"
            color="inherit"
            :size="32"
            class="t:mt-24 t:mb-8"
          />
          {{ t('user_info.no_teams') }}
        </div>
        <div
          v-else
          class="t:space-y-16"
        >
          <CardBlock
            v-for="item in userInfo?.teams_tree"
            :key="item.id"
            no-data-icon="users"
            :no-data-text="t('user_info.no_pinned_affiliates')"
          >
            <template v-slot:blockTitle>
              <CardTitle>
                <template v-slot:title>
                  <RouterLink
                    :to="`/teams/${item.id}/details/users`"
                    class="t:flex-none t:cursor-pointer t:truncate t:font-medium t:text-inherit t:no-underline"
                  >
                    {{ item.name }}
                  </RouterLink>
                </template>
                <UiAction
                  icon="times"
                  :action-name="t('user_info.delete')"
                  color="var(--text-negative)"
                  class="t:ml-16 t:font-medium"
                  @click="delFromTeamHandler(item.id, item.name)"
                />
                <UiAction
                  icon="link"
                  :action-name="t('user_info.pin')"
                  color="var(--text-primary)"
                  class="t:ml-16 t:font-medium"
                  @click="pinAffiliatesHandler(item.id)"
                />
              </CardTitle>
            </template>
            <template
              v-if="item.affiliates.length"
              v-slot:cardItem
            >
              <CardItem
                v-for="(affiliate, key) in item.affiliates"
                :key="affiliate.id"
                :no-border="key === 0"
              >
                <template v-slot:title>
                  {{ affiliate.email }}
                </template>
                <template v-slot:value>
                  <UiAction
                    icon="unlink"
                    :action-name="t('user_info.unpin')"
                    color="var(--text-negative)"
                    @click="unpinAffiliateHandler(affiliate.id, affiliate.email, item.id)"
                  />
                </template>
              </CardItem>
            </template>
          </CardBlock>
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="scss">
.user-info {
  &__block {
    flex-basis: 100%;
    &_side {
      flex-shrink: 0;
      flex-basis: 300px;
      width: 300px;
    }
  }
  &__last-seen {
    font-size: 12px;
    span {
      font-weight: 600;
      color: var(--text-negative);
    }
  }
  &__empty {
    display: flex;
    flex-direction: column;
    align-items: center;
    font-size: 20px;
    color: var(--text-body-tertiary);
    width: 100%;
  }
}
</style>
